import { makeAutoObservable, runInAction } from 'mobx';
import { apiStore } from './ApiStore';
import type {
  PlanItem,
  SubscriptionProcessResponse,
  SubscriptionStatus,
  SubscriptionStatusResponse,
} from '@/types/api';
import { config } from '@/config/env';

const isSubscriptionProcessResponse = (
  response: SubscriptionStatusResponse,
): response is SubscriptionProcessResponse => typeof response.status === 'object';

const getSubscriptionStatus = (response: SubscriptionStatusResponse | null): SubscriptionStatus | null => {
  if (!response) {
    return null;
  }

  return isSubscriptionProcessResponse(response) ? response.status : response;
};

export class SubscriptionStore {
  status: SubscriptionStatus | null = null;
  plans: PlanItem[] = [];
  loading = false;
  error: string | null = null;
  selectedTrialPlanId: string | null = null;
  statusOwnerEmail: string | null = null;
  private fetchPlansPromise: Promise<void> | null = null;
  private identityVersion = 0;

  constructor(private api: typeof apiStore) {
    makeAutoObservable(this);
  }

  get hasAccess(): boolean {
    return this.statusOwnerEmail === this.api.user?.email
      && this.status?.hasAccess === true;
  }

  get trialPlans(): PlanItem[] {
    return this.plans.filter((plan) => Boolean(plan.trial));
  }

  get selectedTrialPlan(): PlanItem | null {
    if (!this.trialPlans.length) {
      return null;
    }

    if (this.selectedTrialPlanId) {
      const existing = this.trialPlans.find((plan) => plan.planId === this.selectedTrialPlanId);
      if (existing) {
        return existing;
      }
    }

    return this.trialPlans[0];
  }

  setSelectedTrialPlan(planId: string): void {
    if (this.trialPlans.some((plan) => plan.planId === planId)) {
      this.selectedTrialPlanId = planId;
    }
  }

  applyStatus(status: SubscriptionStatus): void {
    this.status = status;
    this.statusOwnerEmail = this.api.user?.email ?? null;
    this.error = null;
  }

  resetForIdentityChange(): void {
    this.identityVersion += 1;
    this.status = null;
    this.statusOwnerEmail = null;
    this.error = null;
    this.loading = false;
    this.selectedTrialPlanId = null;
  }

  async fetchStatus(force = false): Promise<void> {
    // Protection from duplicate requests
    const ownerEmail = this.api.user?.email ?? null;
    const identityVersion = this.identityVersion;
    if (
      this.loading
      || (
        this.status
        && this.statusOwnerEmail === ownerEmail
        && !force
      )
    ) {
      return;
    }

    try {
      runInAction(() => {
        this.loading = true;
        this.error = null;
      });

      const res = await this.api.getSubscriptionStatus();

      runInAction(() => {
        if (identityVersion !== this.identityVersion) {
          return;
        }
        // API returns {status: {...}}, we need just the inner data
        this.status = getSubscriptionStatus(res);
        this.statusOwnerEmail = ownerEmail;
      });
    }
    catch {
      runInAction(() => {
        if (identityVersion !== this.identityVersion) {
          return;
        }
        this.error = 'failed_status';
        this.status = null;
        this.statusOwnerEmail = ownerEmail;
      });
    }
    finally {
      runInAction(() => {
        if (identityVersion === this.identityVersion) {
          this.loading = false;
        }
      });
    }
  }

  async fetchPlans(): Promise<void> {
    // If already loading, wait for the existing request to complete
    if (this.loading && this.fetchPlansPromise) {
      return this.fetchPlansPromise;
    }

    // If plans are already loaded, return immediately
    if (this.plans.length > 0) {
      return;
    }

    this.fetchPlansPromise = (async () => {
      try {
        runInAction(() => {
          this.loading = true;
          this.error = null;
        });

        // Используем обычный fetch вместо axios
        const response = await fetch(`${config.apiBase}v1/subscription/plans`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(apiStore.accessToken && {
              Authorization: `Bearer ${apiStore.accessToken}`,
            }),
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const res = (await response.json()) as { items: PlanItem[] };

        runInAction(() => {
          this.plans = res.items || [];

          const defaultTrialPlan
            = this.trialPlans.find((plan) => plan.billingFrequency === 'weekly')
              ?? this.trialPlans[0]
              ?? null;

          if (
            !this.selectedTrialPlanId
            || !this.trialPlans.some((plan) => plan.planId === this.selectedTrialPlanId)
          ) {
            this.selectedTrialPlanId = defaultTrialPlan ? defaultTrialPlan.planId : null;
          }
        });
      }
      catch (error) {
        console.error('fetchPlans error:', error);
        runInAction(() => {
          this.error = 'failed_plans';
          this.plans = [];
        });
      }
      finally {
        runInAction(() => {
          this.loading = false;
          this.fetchPlansPromise = null;
        });
      }
    })();

    return this.fetchPlansPromise;
  }
}

export const subscriptionStore = new SubscriptionStore(apiStore);
